import React, { lazy } from 'react';

import PAGE_CONSTANTS, { DEFAULT_PAGE_URI } from '../pages/PAGE_CONSTANTS';
import environment from '@/environments/environment';
import { ErrorResponse } from 'react-router-dom';

const { IS_DOMAIN_FEDERATION_ENABLED } = environment;

// Data and Security pages
const App = lazy(() => import('@/App'));

// Data and Security pages
const DataSecurityHomePage = lazy(() => import('@/pages/DataSecurityPage/DataSecurityPage'));

// Data and Security error pages
const ErrorPage = lazy(() => import('@/pages/ErrorPage/ErrorPage'));

// Federations pages
const DomainFederationsPage = lazy(
  () => import('@/pages/DomainFederationsPage/DomainFederationsPage')
);
const UpdateFederationPage = lazy(
  () => import('@/pages/DomainFederationsPage/UpdateFederationPage/UpdateFederationPage')
);
const CreateFederationPage = lazy(
  () => import('@/pages/DomainFederationsPage/CreateFederationPage/CreateFederationPage')
);
const ManageFederationsPage = lazy(
  () => import('@/pages/DomainFederationsPage/ManageFederationsPage/ManageFederationsPage')
);

const generateRouterRoutes = (routes: Array<any>): any[] =>
  routes.map((route) => {
    const { index, path, element, children } = route;
    return {
      path,
      element,
      children: children ? generateRouterRoutes(children) : undefined,
      key: route.name,
      index,
    };
  });

const generateFullPathPages = (
  rootStates: any[],
  parentPath: string,
  pathContainer: Map<string, string>
) => {
  rootStates?.forEach((state) => {
    const { path, name } = state;
    let pathWithoutQueries = trimQueriesFromUrl(path);
    if (pathWithoutQueries[0] !== '/' && !state.index) {
      pathWithoutQueries = '/' + pathWithoutQueries;
    }
    if (parentPath[parentPath.length - 1] === '/' && pathWithoutQueries[0] === '/') {
      pathWithoutQueries = pathWithoutQueries.substring(1);
    }
    pathContainer.set(name ?? '', `${parentPath}${pathWithoutQueries}`);

    const childStates = state.children;
    if (childStates?.length > 0) {
      generateFullPathPages(childStates, pathContainer.get(name ?? '') ?? '', pathContainer);
    }
  });
  return pathContainer;
};

/**
 * Trim query params off an url to work with react-router-dom
 * Query params MUST be specified to work with ui-router, as such: '?param1&param2'
 * @param url the url to trim
 */
export const trimQueriesFromUrl = (url?: string) => url?.split('?')[0] ?? '';

/**
 * Configuration object returned by createRouteConfig factory function
 */
interface RouteConfig {
  routesReactRouter: any[];
  fullPathPages: Map<string, string>;
  getUrlFromPageName: (pageName: string) => string;
}

/**
 * USAGE GUIDE:
 *
 * ## Microfrontend (Dynamic BasePath)
 * In AppRemote.tsx, the host application can pass basePath:
 * ```
 * <AppRemote basePath="/custom-path" />
 * ```
 *
 * ## Component Usage
 * Use the custom hooks for route utilities (automatically uses context basePath):
 * ```
 * import { useGetUrlFromPageName } from '@/hooks/useRouteConfig';
 * import { useNavigate } from '@/routers/useNavigate';
 *
 * const MyComponent = () => {
 *   const getUrlFromPageName = useGetUrlFromPageName();
 *   const navigate = useNavigate();
 *
 *   const homeUrl = getUrlFromPageName(PAGE_CONSTANTS.HOME);
 *   const goToHome = () => navigate({ pageName: PAGE_CONSTANTS.HOME });
 * };
 * ```
 *
 * ## Backward Compatibility
 * Static exports are still available for gradual migration:
 * ```
 * import { getUrlFromPageName } from '@/routers/RouteConfig';
 * ```
 */

/**
 * Factory function to create route configuration with dynamic parentPath
 * @param parentPath The parent path for the microfrontend (e.g., '/app', '/data-security')
 * @returns RouteConfig object with routes and utility functions
 */
export const createRouteConfig = (parentPath: string = ''): RouteConfig => {
  /**
   * RoutingStates is an array of ReactStateDeclaration objects.
   * Each object represents a route in the application.
   */
  const routes: Array<any> = [
    {
      path: '/',
      element: <App />,
      name: PAGE_CONSTANTS.APP,
      children: [
        {
          index: true,
          element: <DataSecurityHomePage />,
          name: PAGE_CONSTANTS.HOME,
        },
        {
          path: '*',
          element: <ErrorPage error={{ status: 404 } as ErrorResponse} />,
          name: PAGE_CONSTANTS.NOT_FOUND,
        },
      ],
    },
  ];

  if (IS_DOMAIN_FEDERATION_ENABLED) {
    routes[0].children.push({
      path: 'federations',
      element: <DomainFederationsPage />,
      name: PAGE_CONSTANTS.FEDERATIONS,
      children: [
        {
          index: true,
          element: <ManageFederationsPage />,
          name: PAGE_CONSTANTS.MANAGE_FEDERATIONS,
        },
        {
          path: 'create',
          element: <CreateFederationPage />,
          name: PAGE_CONSTANTS.CREATE_FEDERATION,
        },
        {
          path: ':id',
          element: <UpdateFederationPage />,
          name: PAGE_CONSTANTS.UPDATE_FEDERATION,
        },
      ],
    });
  }
   //console.log("======env values", environment.IS_RUNNING_STAND_ALONE, environment.IS_PRODUCTION);
  //remove environment.IS_PRODUCTION for stand-alone mode to redirect to the default page data-security
  const isAccountSecurityDomain = window.location.hostname.includes('accountsecurity');
  if (
    environment.IS_RUNNING_STAND_ALONE &&
    (isAccountSecurityDomain ? !environment.IS_PRODUCTION : true)
  ) {
    routes[0].path = DEFAULT_PAGE_URI;
  }

  const routesReactRouter = generateRouterRoutes(routes);
  const rootStates = routes.filter((state) => state.name === PAGE_CONSTANTS.APP);
  const fullPathPages = generateFullPathPages(rootStates, parentPath, new Map<string, string>());

  return {
    routesReactRouter,
    fullPathPages,
    getUrlFromPageName: (pageName: string) => fullPathPages.get(pageName) ?? '',
  };
};

/**
 * Default configuration for backward compatibility
 * Uses the environment-based parentPath logic
 */
const defaultParentPath = environment.IS_RUNNING_STAND_ALONE ? '' : '/app';
const defaultConfig = createRouteConfig(defaultParentPath);

/**
 * Default exports for backward compatibility during migration
 */
export const { routesReactRouter, fullPathPages, getUrlFromPageName } = defaultConfig;
export default routesReactRouter;
